<script setup lang="ts">
  import * as z from 'zod';
  import { computed, unref } from 'vue';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type {
    HeaderProperties as HeaderPropsType,
    Spacing,
    TableProperties as TablePropertiesType,
  } from '../../../model/form.types';
  import { useFormBuilderStore } from '../../../model/form-builder.store';
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/ui';
  import { Input } from '@/shared/ui';
  import MarginControl from './MarginControl.vue';
  import { DEFAULT_HEADER_MARGIN } from '../../../lib/element-defaults';

  type HeaderOrLegacyTableProperties = HeaderPropsType | TablePropertiesType;

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  const formBuilderStore = useFormBuilderStore();
  const selectedHeaderPageIndex = computed(
    () => unref(formBuilderStore.selectedHeaderPageIndex) ?? 0
  );

  type FormValues = {
    name: string;
    margin: Spacing;
  };

  const { values, setValues } = usePropertyForm<FormValues, HeaderOrLegacyTableProperties>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(100, 'Máximo de 100 caracteres'),
      margin: z.object({
        top: z.number(),
        right: z.number(),
        bottom: z.number(),
        left: z.number(),
      }),
    }),
    extractInitialValues: (properties: HeaderOrLegacyTableProperties | undefined): FormValues => {
      const pageIndex = selectedHeaderPageIndex.value;
      const byPage = (properties as HeaderPropsType)?.marginByPage;
      const singleMargin = (properties as TablePropertiesType)?.margin;
      const pageMargin = (byPage && byPage[pageIndex]) ?? singleMargin ?? DEFAULT_HEADER_MARGIN;
      return {
        name: properties?.name ?? '',
        margin: { ...DEFAULT_HEADER_MARGIN, ...pageMargin },
      };
    },
    mapValuesToProperties: (
      values: FormValues,
      currentProperties: HeaderOrLegacyTableProperties
    ) => {
      const pageIndex = selectedHeaderPageIndex.value;
      const headerProps = currentProperties as HeaderPropsType;
      const useMarginByPage = headerProps.marginByPage != null;
      const marginByPage = {
        ...(headerProps.marginByPage ?? {}),
        [pageIndex]: values.margin,
      };
      return {
        properties: {
          ...currentProperties,
          name: values.name || currentProperties.name,
          ...(useMarginByPage ? { marginByPage } : { margin: values.margin }),
        },
      };
    },
  });

  const marginModel = computed({
    get: () => unref(values)?.margin ?? DEFAULT_HEADER_MARGIN,
    set: (v: Spacing) => setValues({ ...unref(values), margin: v } as FormValues),
  });

  function onMarginUpdate(v: Spacing) {
    setValues({ ...unref(values), margin: v } as FormValues);
  }
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Cabeçalho 01"
              class="h-11 disabled:bg-gray-100 disabled:text-gray-07/90"
              :disabled="true"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-2">
      <p class="text-sm font-semibold text-gray-11">
        Espaçamento
      </p>
      <MarginControl
        :model-value="marginModel"
        :element-id="elementId"
        @update:model-value="onMarginUpdate"
      />
    </div>
  </div>
</template>
